/**
 * WIPApp – Winter Impact Predictions Application
 *
 * Two independent data flows sharing the same storm selection:
 *
 *   Region view  (N/S + E/W sliders only):
 *     region_metadata → region_impacts → GeoJSON polygons rendered directly
 *
 *   District view (4 sliders: N/S, E/W, Precipitation, Wind Gust):
 *     wip_metadata → wip_scenario (→ wip_scenario_metadata_id) → wip_impacts
 *     + storm_impact_models for dynamic legend / table columns
 *
 * Storms are loaded via the shared /api/v1/storms endpoint filtered by
 * storm_application_id (the WIP application row).
 */

import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { Snowflake, ChevronDown, Check, Map, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import { MapLoaderOverlay } from "@/components/ui/loader";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { BaseMap, GeoJSONLayer } from "@/components/maps/LeafletMap";
import {
  WIPScenarioControls,
  WIPImpactsTable,
  MapLegend,
  MapPropertySelector,
} from "@/components/maps/insets";
import type { PathOptions } from "leaflet";
import {
  useStorms,
  useStormImpactModels,
  useDisplayableApplications,
} from "@/hooks/api";
import {
  useWIPMetadata,
  useWIPScenario,
  useWIPImpacts,
  useRegionMetadata,
  useRegionImpacts,
} from "@/hooks/api/useWIP";
import type {
  NamedStorm,
  ScenarioParameterValues,
  WIPScenarioDeviations,
  WIPImpactForecast,
  StormImpactModelDetailed,
  StormImpactVariable,
  StormImpactVariableBin,
} from "@/api/types";
import type { ApiError } from "@/api";

// ─── Defaults ────────────────────────────────────────────────────────────────

const DEFAULT_DEVIATIONS: WIPScenarioDeviations = {
  shift_northsouth: 0,
  shift_eastwest: 0,
  shift_precipitation: 0,
  shift_wind_gust: 0,
};

/** Region GeoJSON features use properties.PTYPE (not impact_level). */
const REGION_PTYPE_COLORS: Record<string, string> = {
  Rain: "#2563eb",
  Freezing_Rain: "#7c3aed",
  Sleet: "#db2777",
  Wet_Snow: "#0891b2",
  Dry_Snow: "#64748b",
};

const REGION_PTYPE_ORDER = [
  "Rain",
  "Freezing_Rain",
  "Sleet",
  "Wet_Snow",
  "Dry_Snow",
] as const;

const DEFAULT_REGION_FILL = "#93c5fd";

function formatPtypeLabel(ptype: string): string {
  return ptype.replace(/_/g, " ");
}

function getRegionPtypeColor(ptype: unknown): string {
  if (typeof ptype !== "string" || !ptype) return DEFAULT_REGION_FILL;
  return REGION_PTYPE_COLORS[ptype] ?? DEFAULT_REGION_FILL;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getColorForValue(
  value: number,
  bins: { min: number; max: number; color: string }[],
): string {
  const bin = bins.find((b) => value >= b.min && value < b.max);
  return bin?.color ?? "#d1d5db";
}

/**
 * The WIP impacts API returns variable_values with underscore keys
 * (e.g. `dry_snow`, `customers_interrupted`) while the storm impact model
 * stores variable_slug values with hyphens (e.g. `dry-snow`,
 * `customers-interrupted`).  This helper normalises a model slug to the
 * API key format so look-ups against variable_values work correctly.
 */
const toApiKey = (slug: string) => slug.replace(/-/g, "_");

/** Formats a raw snake_case API key into a human-readable label. */
const formatVariableLabel = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const sanitizeTooltipText = (value: unknown): string =>
  DOMPurify.sanitize(String(value ?? ""), {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });

const pickScenarioParams = (
  values: ScenarioParameterValues,
  keys: string[],
): ScenarioParameterValues =>
  Object.fromEntries(keys.map((key) => [key, values[key] ?? 0]));

// ─── Component ───────────────────────────────────────────────────────────────

export interface WIPAppProps {
  appSlug?: string;
}

export default function WIPApp({ appSlug = "wip" }: WIPAppProps) {
  // ── Core State ────────────────────────────────────────────────────────────
  const [selectedStorm, setSelectedStorm] = useState<NamedStorm | null>(null);
  const [isCompactLayout, setIsCompactLayout] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 1023px)").matches
      : false,
  );
  const [viewMode, setViewMode] = useState<"region" | "district">("region");
  const [selectedProperty, setSelectedProperty] = useState<string>("");
  const [selectedImpactModel, setSelectedImpactModel] =
    useState<StormImpactModelDetailed | null>(null);
  const [legendCollapsed, setLegendCollapsedInternal] = useState(false);
  // Tracks whether the user has explicitly toggled the legend. Once set, the
  // breakpoint listener stops overriding the user's choice.
  const userHasToggledLegendRef = useRef(false);
  const setLegendCollapsed = useCallback((value: boolean) => {
    userHasToggledLegendRef.current = true;
    setLegendCollapsedInternal(value);
  }, []);

  /** Local slider values (not yet committed). */
  const [localDeviations, setLocalDeviations] =
    useState<ScenarioParameterValues>(DEFAULT_DEVIATIONS);
  /** Committed values that drive data fetching. */
  const [committedDeviations, setCommittedDeviations] =
    useState<ScenarioParameterValues>(DEFAULT_DEVIATIONS);

  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const [isViewTransition, setIsViewTransition] = useState(false);
  const [scenarioError, setScenarioError] = useState<string | null>(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1023px)");
    const handleChange = () => setIsCompactLayout(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    if (!userHasToggledLegendRef.current) {
      setLegendCollapsedInternal(isCompactLayout);
    }
  }, [isCompactLayout]);

  // ── Application ID resolution ─────────────────────────────────────────────
  const { data: applications, loading: applicationsLoading } =
    useDisplayableApplications();
  const applicationId = useMemo(() => {
    if (applications?.length && appSlug) {
      const app = applications.find((a) => a.slug === appSlug);
      if (app?.id) return app.id;
    }
    return null;
  }, [applications, appSlug]);

  // ── Storms (shared endpoint with storm_application_id filter) ────────────
  const { data: storms = [], loading: stormsLoading } = useStorms(
    false,
    applicationId,
  );

  useEffect(() => {
    if (storms.length > 0 && !selectedStorm) {
      setSelectedStorm(storms.find((s) => s.active) ?? storms[0]);
    }
  }, [storms]);

  const stormId = selectedStorm?.id ?? null;
  const districtStormId = viewMode === "district" ? stormId : null;
  const regionStormId = viewMode === "region" ? stormId : null;
  const districtApplicationId = viewMode === "district" ? applicationId : null;
  const regionApplicationId = viewMode === "region" ? applicationId : null;

  // ── Impact Models (same as HIP — dynamic legend & table columns) ─────────
  const { data: impactModels = [], loading: impactModelsLoading } =
    useStormImpactModels(districtApplicationId, { useFallback: false });

  useEffect(() => {
    if (impactModels.length === 0) return;
    const selectedStillExists = impactModels.some(
      (m) => m.model_id === selectedImpactModel?.model_id,
    );
    if (!selectedStillExists) {
      const wipModel =
        impactModels.find((m) => m.model_slug === "wip-research") ??
        impactModels[0];
      setSelectedImpactModel(wipModel);
    }
  }, [impactModels, selectedImpactModel]);

  // ── Metadata (both fetched upfront — lightweight & cached) ────────────────
  const { data: wipMetadata, loading: wipMetaLoading } = useWIPMetadata(
    districtStormId,
    districtApplicationId,
  );
  const { data: regionMetadata, loading: regionMetaLoading } =
    useRegionMetadata(regionStormId, regionApplicationId);

  const districtParameterKeys = useMemo(
    () =>
      wipMetadata?.parameters.map((parameter) => parameter.key) ??
      Object.keys(DEFAULT_DEVIATIONS),
    [wipMetadata],
  );

  const regionParameterKeys = useMemo(
    () =>
      regionMetadata?.parameters.map((parameter) => parameter.key) ?? [
        "shift_northsouth",
        "shift_eastwest",
      ],
    [regionMetadata],
  );

  // ── Region Flow ───────────────────────────────────────────────────────────
  const regionParams = useMemo(() => {
    if (viewMode !== "region") return null;
    return pickScenarioParams(committedDeviations, regionParameterKeys) as {
      shift_northsouth: number;
      shift_eastwest: number;
    };
  }, [viewMode, committedDeviations, regionParameterKeys]);

  const {
    data: regionImpact,
    loading: regionLoading,
    isFetching: regionFetching,
  } = useRegionImpacts(regionStormId, regionApplicationId, regionParams);

  // ── District (WIP) Flow ───────────────────────────────────────────────────
  const wipScenarioParams = useMemo(() => {
    if (viewMode !== "district") return null;
    return pickScenarioParams(committedDeviations, districtParameterKeys);
  }, [viewMode, committedDeviations, districtParameterKeys]);

  const {
    data: wipScenario,
    loading: wipScenarioLoading,
    isFetching: wipScenarioFetching,
    error: wipScenarioError,
  } = useWIPScenario(districtStormId, districtApplicationId, wipScenarioParams);

  useEffect(() => {
    if (!wipScenarioError) {
      setScenarioError(null);
      return;
    }
    const err = wipScenarioError as ApiError;
    const status = err?.status ?? (err as any)?.response?.status;
    if (status === 422) {
      setScenarioError(
        "Selected shift combination exceeds forecast bounds. Adjust the sliders and try again.",
      );
    } else {
      setScenarioError(null);
    }
  }, [wipScenarioError]);

  const scenarioMetadataId =
    scenarioError || viewMode !== "district"
      ? null
      : (wipScenario?.scenario_metadata_id ?? null);

  const {
    data: wipImpacts = [],
    loading: wipImpactsLoading,
    isFetching: wipImpactsFetching,
    scenario: wipImpactScenario,
  } = useWIPImpacts(scenarioMetadataId);

  // ── Loading States ────────────────────────────────────────────────────────
  const isRemoteDataLoading =
    viewMode === "region"
      ? regionLoading || regionFetching
      : wipScenarioLoading ||
        wipScenarioFetching ||
        wipImpactsLoading ||
        wipImpactsFetching;

  const isDataLoading = isRemoteDataLoading || isRendering;

  const isBootstrapLoading =
    applicationsLoading ||
    !applicationId ||
    !selectedStorm ||
    stormsLoading ||
    (viewMode === "region"
      ? regionMetaLoading || regionLoading
      : impactModelsLoading ||
        wipMetaLoading ||
        wipScenarioLoading ||
        wipImpactsLoading);

  /**
   * True whenever district-specific data is in flight — covers both the
   * initial district load (when hasLoadedOnce is already true from region
   * mode) and subsequent scenario refreshes.  Used to drive the loading
   * states of district-only insets so they show spinners independently of
   * the full-screen MapLoaderOverlay.
   */
  const isDistrictLoading =
    viewMode === "district" &&
    (impactModelsLoading ||
      wipMetaLoading ||
      wipScenarioLoading ||
      wipScenarioFetching ||
      wipImpactsLoading ||
      wipImpactsFetching);

  const activeViewLoading =
    viewMode === "region"
      ? regionMetaLoading || regionLoading || regionFetching
      : isDistrictLoading;
  const viewTransitionLoading = isViewTransition && activeViewLoading;

  useEffect(() => {
    if (hasLoadedOnce || !selectedStorm || isBootstrapLoading) return;
    setHasLoadedOnce(true);
  }, [hasLoadedOnce, selectedStorm, isBootstrapLoading]);

  useEffect(() => {
    if (isRendering && !isRemoteDataLoading) {
      const t = setTimeout(() => setIsRendering(false), 200);
      return () => clearTimeout(t);
    }
  }, [isRendering, isRemoteDataLoading]);

  useEffect(() => {
    if (!isViewTransition || activeViewLoading) return;
    setIsViewTransition(false);
  }, [isViewTransition, activeViewLoading]);

  // ── Region GeoJSON ────────────────────────────────────────────────────────
  const regionGeoJSON = useMemo(() => {
    if (!regionImpact?.geometry_json) return null;
    return regionImpact.geometry_json;
  }, [regionImpact]);

  const regionDataKey = useMemo(() => {
    if (!regionImpact) return "no-region";
    return `region-${regionImpact.region_impact_id}-${JSON.stringify(
      regionImpact.shift_params,
    )}`;
  }, [regionImpact]);

  // ── Legend Config (region = PTYPE; district = impact model bins) ──────────
  const legendConfig = useMemo(() => {
    if (viewMode === "region") {
      const features =
        (regionGeoJSON as { features?: Array<{ properties?: { PTYPE?: string } }> } | null)
          ?.features ?? [];
      const present = new Set(
        features
          .map((f) => f?.properties?.PTYPE)
          .filter((p): p is string => typeof p === "string" && p.length > 0),
      );
      const ordered = REGION_PTYPE_ORDER.filter((t) => present.has(t));
      const extras = [...present].filter(
        (t) => !REGION_PTYPE_ORDER.includes(t as (typeof REGION_PTYPE_ORDER)[number]),
      );
      const types = present.size > 0 ? [...ordered, ...extras] : [...REGION_PTYPE_ORDER];

      return {
        label: "Precipitation Type",
        units: "",
        bins: types.map((ptype, index) => ({
          min: index,
          max: index + 1,
          color: getRegionPtypeColor(ptype),
          label: formatPtypeLabel(ptype),
        })),
      };
    }

    if (!selectedProperty) {
      return { label: "Impact Risk", units: "", bins: [] as any[] };
    }

    // Try to find the variable definition in the impact model for bins + metadata.
    const variable = selectedImpactModel?.variables.find(
      (v: StormImpactVariable) => toApiKey(v.variable_slug) === selectedProperty,
    );

    if (variable) {
      return {
        label: variable.variable_name,
        units: variable.variable_units ?? "",
        bins: variable.bins.map((b: StormImpactVariableBin) => {
          // Sentinel bounds from variable_bins (±1e10) mean open-ended ranges.
          const isFirst = b.bin_min <= -999999990;
          const isLast = b.bin_max == null || b.bin_max >= 999999990;
          const min = isFirst ? -Infinity : b.bin_min;
          const max = isLast ? Infinity : (b.bin_max ?? Infinity);
          // Prefer API bin_label (same as SIP); fall back to a clean range string.
          const label =
            b.bin_label?.trim() ||
            (isLast
              ? `${b.bin_min.toLocaleString()}+`
              : isFirst
                ? `≤ ${b.bin_max!.toLocaleString()}`
                : `${b.bin_min.toLocaleString()} – ${b.bin_max!.toLocaleString()}`);
          return { min, max, color: b.bin_color ?? "#d1d5db", label };
        }),
      };
    }

    // Fallback: no model variable definition — format the raw key as label.
    return {
      label: formatVariableLabel(selectedProperty),
      units: "",
      bins: [] as any[],
    };
  }, [viewMode, regionGeoJSON, selectedImpactModel, selectedProperty]);

  const getRegionStyle = useCallback((feature: any): PathOptions => {
    const ptype = feature?.properties?.PTYPE;
    return {
      color: "#6b7280",
      weight: 1.5,
      opacity: 1,
      fillColor: getRegionPtypeColor(ptype),
      fillOpacity: 0.55,
    };
  }, []);

  const buildRegionTooltip = useCallback((feature: any): string => {
    const ptypeRaw = feature?.properties?.PTYPE;
    const ptype =
      typeof ptypeRaw === "string" && ptypeRaw
        ? formatPtypeLabel(ptypeRaw)
        : "Unknown";
    const label = sanitizeTooltipText(ptype);
    const swatch = getRegionPtypeColor(ptypeRaw);

    return `
      <div class="weather-threat-tooltip">
        <div class="threat-tooltip-header">
          <h3 class="threat-tooltip-title">Precipitation Type</h3>
        </div>
        <div class="selected-variable-section">
          <div class="selected-variable-display" style="display:flex;align-items:center;gap:8px;">
            <span style="display:inline-block;width:12px;height:12px;border-radius:2px;background:${swatch};border:1px solid #9ca3af;flex-shrink:0;"></span>
            <span class="selected-variable-data">${label}</span>
          </div>
        </div>
      </div>
    `;
  }, []);

  const onEachRegionFeature = useCallback(
    (feature: any, layer: any) => {
      layer.bindPopup(() => buildRegionTooltip(feature), {
        maxWidth: isCompactLayout ? 220 : 260,
        minWidth: isCompactLayout ? 160 : 200,
        autoPan: false,
        closeButton: false,
        className: "weather-threat-popup",
      });
      layer.on({
        mouseover: (e: any) => {
          e.target.setStyle({ weight: 2.5, fillOpacity: 0.8 });
          e.target.bringToFront();
          layer.openPopup(e.latlng);
        },
        mouseout: (e: any) => {
          e.target.setStyle(getRegionStyle(feature));
          layer.closePopup();
        },
        mousemove: (e: any) => {
          layer.getPopup()?.setLatLng(e.latlng);
        },
      });
    },
    [buildRegionTooltip, getRegionStyle, isCompactLayout],
  );

  // ── District GeoJSON ──────────────────────────────────────────────────────
  const impactsByNodeId = useMemo(() => {
    const m: Record<string, WIPImpactForecast> = {};
    for (const i of wipImpacts) m[i.geographic_node_id] = i;
    return m;
  }, [wipImpacts]);

  const impactsByNodeIdRef = useRef(impactsByNodeId);
  impactsByNodeIdRef.current = impactsByNodeId;

  const wipPropertySelectorRef = useRef<HTMLDivElement>(null);

  const districtGeoJSON = useMemo(() => {
    if (!wipImpacts.length) return null;
    const features = wipImpacts
      .filter((i) => i.geometry)
      .map((i) => ({
        type: "Feature",
        geometry: i.geometry,
        properties: {
          id: i.geographic_node_id,
          name: i.geographic_node_name,
          ...i.variable_values,
        },
      }));
    if (!features.length) return null;
    return { type: "FeatureCollection", features };
  }, [wipImpacts]);

  const districtDataKey = useMemo(() => {
    if (!wipImpacts.length) return "no-districts";
    return (
      `${scenarioMetadataId ?? "no-scenario"}-` +
      wipImpacts
        .slice(0, 15)
        .map((i) => i.geographic_node_id)
        .join("-")
        .slice(0, 120) +
      `-${selectedProperty}`
    );
  }, [wipImpacts, selectedProperty, scenarioMetadataId]);

  const getDistrictStyle = useCallback(
    (feature: any): PathOptions => {
      const nodeId = feature.properties?.id;
      const impact = impactsByNodeIdRef.current[nodeId];
      const value = impact?.variable_values?.[selectedProperty] ?? 0;
      const bins = legendConfig.bins;
      const fillColor = getColorForValue(Number(value), bins);
      return {
        color: "#9ca3af",
        weight: 1.5,
        opacity: 1,
        fillColor,
        fillOpacity: 0.65,
      };
    },
    [selectedProperty, legendConfig.bins],
  );

  const buildDistrictTooltip = useCallback(
    (props: Record<string, unknown>): string => {
      const nodeId = props?.id;
      const impact = impactsByNodeIdRef.current[nodeId as string];
      const name = sanitizeTooltipText(props?.name ?? "Unknown");
      const hasData = !!impact;
      const selectedValue = hasData
        ? (impact.variable_values[selectedProperty] ?? 0)
        : 0;
      const selectedValueDisplay = sanitizeTooltipText(
        typeof selectedValue === "number"
          ? Math.round(selectedValue).toLocaleString()
          : selectedValue,
      );
      const selectedLabel = sanitizeTooltipText(
        String(legendConfig.label ?? "").toUpperCase(),
      );
      const selectedUnits = sanitizeTooltipText(legendConfig.units);

      return `
        <div class="weather-threat-tooltip">
          <div class="threat-tooltip-header">
            <h3 class="threat-tooltip-title">${name}</h3>
          </div>
          <div class="selected-variable-section">
            ${
              hasData
                ? `<div class="selected-variable-display">
                    <span class="selected-variable-label">${selectedLabel}</span>
                    <span class="selected-variable-data">${selectedValueDisplay}</span>
                    ${selectedUnits ? `<span class="selected-variable-units">${selectedUnits}</span>` : ""}
                   </div>`
                : `<div class="no-data-available">No Impact Data Available</div>`
            }
          </div>
        </div>
      `;
    },
    [selectedProperty, legendConfig],
  );

  const onEachDistrictFeature = useCallback(
    (feature: any, layer: any) => {
      const props = feature.properties;

      layer.bindPopup(() => buildDistrictTooltip(props), {
        maxWidth: isCompactLayout ? 240 : 280,
        minWidth: isCompactLayout ? 190 : 220,
        autoPan: false,
        closeButton: false,
        className: "weather-threat-popup",
      });
      layer.on({
        mouseover: (e: any) => {
          e.target.setStyle({ weight: 3, color: "#374151", fillOpacity: 0.9 });
          e.target.bringToFront();
          layer.openPopup(e.latlng);
        },
        mouseout: (e: any) => {
          e.target.setStyle(getDistrictStyle(feature));
          layer.closePopup();
        },
        mousemove: (e: any) => {
          layer.getPopup()?.setLatLng(e.latlng);
        },
      });
    },
    [buildDistrictTooltip, getDistrictStyle, isCompactLayout],
  );

  // ── Property Selector (district mode — dynamic from impact model) ────────
  const availableImpactVariableSlugs = useMemo(() => {
    const keys = new Set<string>();
    wipImpacts.forEach((i) => {
      Object.entries(i.variable_values ?? {}).forEach(([k, v]) => {
        if (v !== null && v !== undefined) keys.add(k);
      });
    });
    return keys;
  }, [wipImpacts]);

  const propertyOptions = useMemo(() => {
    // Primary: derive options from the registered storm impact model variables,
    // matched against the actual API keys returned by the impacts endpoint.
    if (selectedImpactModel) {
      const modelOptions = selectedImpactModel.variables
        .filter((v: StormImpactVariable) => v.bins?.length > 0)
        .filter((v: StormImpactVariable) =>
          availableImpactVariableSlugs.has(toApiKey(v.variable_slug)),
        )
        .sort(
          (a: StormImpactVariable, b: StormImpactVariable) =>
            (a.variable_order || 0) - (b.variable_order || 0),
        )
        .map((v: StormImpactVariable) => ({
          id: toApiKey(v.variable_slug),
          label: v.variable_name,
          units: v.variable_units ?? "",
          description: v.variable_description ?? "",
          type: v.variable_type_name ?? "",
        }));

      if (modelOptions.length > 0) return modelOptions;
    }

    // Fallback: no impact model or no variable definitions with bins —
    // build options directly from the API response variable_values keys.
    return Array.from(availableImpactVariableSlugs)
      .sort()
      .map((key) => ({
        id: key,
        label: formatVariableLabel(key),
        units: "",
        description: "",
        type: "",
      }));
  }, [selectedImpactModel, availableImpactVariableSlugs]);

  useEffect(() => {
    if (propertyOptions.length === 0) return;
    const isValid = propertyOptions.some((p) => p.id === selectedProperty);
    if (isValid) return;
    setSelectedProperty(propertyOptions[0].id);
  }, [propertyOptions, selectedProperty]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleStormSelect = useCallback((storm: NamedStorm) => {
    setSelectedStorm(storm);
    setScenarioError(null);
    setCommittedDeviations(DEFAULT_DEVIATIONS);
    setLocalDeviations(DEFAULT_DEVIATIONS);
  }, []);

  const handleDeviationChange = useCallback((vals: ScenarioParameterValues) => {
    setLocalDeviations(vals);
  }, []);

  const handleRunModel = useCallback((vals: ScenarioParameterValues) => {
    setLocalDeviations(vals);
    setCommittedDeviations(vals);
    setIsRendering(true);
    setScenarioError(null);
  }, []);

  const handlePropertyChange = useCallback((id: string) => {
    setSelectedProperty(id);
  }, []);

  const handleViewModeToggle = useCallback(
    (mode: "region" | "district") => {
      if (mode === viewMode) return;
      setIsViewTransition(true);
      setIsRendering(false);
      setViewMode(mode);
    },
    [viewMode],
  );

  // ── Display helpers ───────────────────────────────────────────────────────
  const showMapOverlay = !hasLoadedOnce
    ? isBootstrapLoading
    : viewTransitionLoading;

  // Show the top-center pill for any in-progress remote fetch after first load.
  // Also fires during district mode bootstrapping (when hasLoadedOnce is true
  // from a prior region load but district data is still arriving).
  const showForecastPill =
    hasLoadedOnce &&
    !viewTransitionLoading &&
    !scenarioError &&
    (isDistrictLoading || (!isBootstrapLoading && isDataLoading));

  const runTimeDisplay = useMemo(() => {
    if (viewMode === "region") {
      if (!regionImpact?.run_time) return undefined;
      try {
        return `Run: ${new Date(regionImpact.run_time).toLocaleString()}`;
      } catch {
        return undefined;
      }
    }
    return undefined;
  }, [viewMode, regionImpact]);

  const impactsTableLoading = (isDataLoading || isDistrictLoading) && hasLoadedOnce;

  const appNotFound =
    !applicationsLoading && applications !== undefined && !applicationId;

  // ── Render ────────────────────────────────────────────────────────────────
  if (appNotFound) {
    return (
      <div className="h-screen flex flex-col overflow-hidden">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center space-y-2">
            <p className="text-lg font-semibold">Application not found</p>
            <p className="text-sm text-muted-foreground">
              The WIP application is not available for your organisation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <Header />

      {/* WIP sub-header */}
      <div className="flex flex-shrink-0 flex-col gap-2 border-b bg-background px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:px-4">
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="min-w-0 truncate text-base font-semibold sm:text-lg">Winter Impact Predictions</h1>
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:mr-2">
          {/* View Mode Toggle */}
          <div className="flex items-center rounded-md border overflow-hidden">
            <button
              onClick={() => handleViewModeToggle("region")}
              disabled={isDistrictLoading}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                viewMode === "region"
                  ? "bg-primary text-primary-foreground"
                  : "bg-background text-muted-foreground hover:bg-muted"
              }`}
              title="Region View – Impact geometry polygons"
            >
              <Map className="h-3.5 w-3.5" />
              Region
            </button>
            <button
              onClick={() => handleViewModeToggle("district")}
              disabled={isDistrictLoading}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                viewMode === "district"
                  ? "bg-primary text-primary-foreground"
                  : "bg-background text-muted-foreground hover:bg-muted"
              }`}
              title="District View – Territory polygons with impact metrics"
            >
              {isDistrictLoading && viewMode === "district" ? (
                <div className="h-3.5 w-3.5 rounded-full border-2 border-primary-foreground/40 border-t-primary-foreground animate-spin" />
              ) : (
                <Building2 className="h-3.5 w-3.5" />
              )}
              District
            </button>
          </div>

          {/* Storm Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="min-w-0 max-w-full gap-2"
                disabled={stormsLoading}
              >
                <Snowflake className="h-4 w-4" />
                <span className="min-w-0 truncate">
                  {selectedStorm?.name ?? "Select Storm"}
                </span>
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              {storms.length === 0 ? (
                <DropdownMenuItem disabled>
                  No storms available
                </DropdownMenuItem>
              ) : (
                <>
                  <DropdownMenuLabel className="text-muted-foreground text-xs">
                    Active Storms
                  </DropdownMenuLabel>
                  {storms.filter((s) => s.active).length === 0 ? (
                    <DropdownMenuItem disabled>
                      No active storms
                    </DropdownMenuItem>
                  ) : (
                    storms
                      .filter((s) => s.active)
                      .map((storm) => (
                        <DropdownMenuItem
                          key={storm.id}
                          onClick={() => handleStormSelect(storm)}
                          className="flex items-center gap-2"
                        >
                          <Check
                            className={`h-4 w-4 ${
                              selectedStorm?.id === storm.id
                                ? "opacity-100"
                                : "opacity-0"
                            }`}
                          />
                          {storm.name} ({storm.year})
                        </DropdownMenuItem>
                      ))
                  )}

                  <DropdownMenuSeparator />

                  <DropdownMenuLabel className="text-muted-foreground text-xs">
                    Inactive Storms
                  </DropdownMenuLabel>
                  {storms.filter((s) => !s.active).length === 0 ? (
                    <DropdownMenuItem disabled>
                      No inactive storms
                    </DropdownMenuItem>
                  ) : (
                    storms
                      .filter((s) => !s.active)
                      .map((storm) => (
                        <DropdownMenuItem
                          key={storm.id}
                          onClick={() => handleStormSelect(storm)}
                          className="flex items-center gap-2"
                        >
                          <Check
                            className={`h-4 w-4 ${
                              selectedStorm?.id === storm.id
                                ? "opacity-100"
                                : "opacity-0"
                            }`}
                          />
                          {storm.name} ({storm.year})
                        </DropdownMenuItem>
                      ))
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main content */}
      <div className="relative flex min-w-0 flex-1 flex-col overflow-y-auto lg:overflow-hidden">
        <div className="relative h-[45dvh] min-h-[320px] shrink-0 lg:min-h-0 lg:flex-1">
          <MapLoaderOverlay
            loading={showMapOverlay}
            text={!hasLoadedOnce ? "Loading..." : "Loading map data..."}
          />

          {showForecastPill && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 bg-background/90 backdrop-blur-sm border border-border rounded-full px-4 py-1.5 shadow-sm flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse" />
              <span className="text-xs text-muted-foreground font-medium">
                {isDistrictLoading && wipImpacts.length === 0
                  ? "Loading district data..."
                  : "Loading scenario..."}
              </span>
            </div>
          )}

          {scenarioError && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-50 bg-destructive/10 text-destructive border border-destructive/30 rounded-full px-4 py-1.5 shadow-sm max-w-sm text-center">
              <span className="text-xs font-medium">{scenarioError}</span>
            </div>
          )}

          {/* Map */}
          <BaseMap center={[39.5, -83.5]} zoom={5} className="h-full w-full">
            {/* Region view — impact geometry polygons */}
            {viewMode === "region" && regionGeoJSON && (
              <GeoJSONLayer
                dataKey={regionDataKey}
                data={regionGeoJSON as any}
                style={getRegionStyle}
                onEachFeature={onEachRegionFeature}
                fitBounds
              />
            )}

            {/* District view — choropleth from WIP impacts */}
            {viewMode === "district" && districtGeoJSON && (
              <GeoJSONLayer
                dataKey={`${districtDataKey}-${isCompactLayout ? "compact" : "full"}`}
                fitBoundsKey={districtDataKey}
                data={districtGeoJSON as any}
                style={getDistrictStyle}
                onEachFeature={onEachDistrictFeature}
                fitBounds
              />
            )}
          </BaseMap>

          {/* ── Insets ── */}

          {/* Scenario Controls (top-left) */}
          {!isCompactLayout && (
            <WIPScenarioControls
              viewMode={viewMode}
              wipMetadata={wipMetadata ?? null}
              regionMetadata={regionMetadata ?? null}
              values={localDeviations}
              onChange={handleDeviationChange}
              onRunModel={handleRunModel}
              isLoading={isDataLoading && hasLoadedOnce}
              trackError={scenarioError}
              position="top-left"
              width="320px"
              title="Scenario Controls"
              subtitle={runTimeDisplay}
              initiallyCollapsed={false}
            />
          )}

          {/* Property Selector (top-right) — district mode only */}
          {viewMode === "district" && (
            <MapPropertySelector
              ref={wipPropertySelectorRef}
              properties={propertyOptions}
              selectedProperty={
                propertyOptions.find((p) => p.id === selectedProperty) ??
                propertyOptions[0]
              }
              onPropertyChange={handlePropertyChange}
              position="top-right"
              loading={isDistrictLoading}
            />
          )}

          {/* Impact Predictions Table (bottom-left) — district mode only */}
          {viewMode === "district" && (
            !isCompactLayout && (
              <WIPImpactsTable
                impacts={wipImpacts}
                loading={impactsTableLoading}
                error={scenarioError}
                position="bottom-left"
                width="500px"
                height="340px"
                title="Impact Predictions"
                initiallyCollapsed={false}
                selectorVariables={propertyOptions.map((p) => ({
                  id: p.id,
                  label: p.label,
                  units: p.units,
                }))}
                modelVariables={selectedImpactModel?.variables.map((v) => ({
                  slug: toApiKey(v.variable_slug),
                  name: v.variable_name,
                  units: v.variable_units,
                  order: v.variable_order,
                }))}
              />
            )
          )}

          {/* Legend (bottom-right):
              region  → derived from geometry_json features[].properties.PTYPE
              district → variable bins from storm-impact-models API */}
          {legendConfig.bins.length > 0 && (
            <MapLegend
              propertyName={legendConfig.label}
              units={legendConfig.units || undefined}
              bins={legendConfig.bins}
              position="bottom-right"
              collapsed={legendCollapsed}
              onToggle={() => setLegendCollapsed(!legendCollapsed)}
              overlapRef={
                viewMode === "district" ? wipPropertySelectorRef : undefined
              }
            />
          )}
        </div>

        {isCompactLayout && (
        <div className="grid min-w-0 gap-3 bg-muted/40 p-3 lg:hidden">
          <WIPScenarioControls
            viewMode={viewMode}
            wipMetadata={wipMetadata ?? null}
            regionMetadata={regionMetadata ?? null}
            values={localDeviations}
            onChange={handleDeviationChange}
            onRunModel={handleRunModel}
            isLoading={isDataLoading && hasLoadedOnce}
            trackError={scenarioError}
            title="Scenario Controls"
            subtitle={runTimeDisplay}
            initiallyCollapsed={false}
            isCollapsible={false}
            panelMode
          />
          {viewMode === "district" && (
            <WIPImpactsTable
              impacts={wipImpacts}
              loading={impactsTableLoading}
              error={scenarioError}
              height="340px"
              title="Impact Predictions"
              initiallyCollapsed={false}
              isCollapsible={false}
              panelMode
              selectorVariables={propertyOptions.map((p) => ({
                id: p.id,
                label: p.label,
                units: p.units,
              }))}
              modelVariables={selectedImpactModel?.variables.map((v) => ({
                slug: toApiKey(v.variable_slug),
                name: v.variable_name,
                units: v.variable_units,
                order: v.variable_order,
              }))}
            />
          )}
        </div>
        )}
      </div>
    </div>
  );
}
